import React, { JSX, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchPurchaseOrderDetail,
  clearCurrentPo,
  approvePurchaseOrder,
  rejectPurchaseOrder,
  submitPurchaseOrder,
  unsubmitPurchaseOrder,
  cancelPurchaseOrder,
  approveCancelPurchaseOrder,
  rejectCancelPurchaseOrder,
  recreatePurchaseOrder,
  deletePurchaseOrder,
  fetchGrns,
  fetchGrnPoItems,
  createGrn,
  fetchGrnDetail,
  updateGrn,
  finalizeGrn,
  removeGrn,
  fetchCreditNotes,
  updateCreditNote,
  createCreditNote,
  deleteCreditNote,
  addCreditNoteLineItem,
  removeCreditNoteLineItem,
  fetchAmendments,
  createAmendment,
  approveAmendment,
  rejectAmendment,
  fetchOrderDeliverySummary,
  PoDetail,
} from './poSlice';
import { AppDispatch, RootState } from '../../app/store';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatUtils';
import AddDocumentModal from './AddDocumentModal';
import EditDocumentModal from './EditDocumentModal';
import { deletePoDocumentApi } from '../../services/api';
import type { AuthUser } from '../auth/authSlice';

const STORE_PREVIEW_LIMIT = 5;

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { current, detailLoading, actionLoading, error } = useSelector(
    (state: RootState) => state.purchaseOrders
  );
  const authUser = useSelector((state: RootState) => state.auth.user as AuthUser | null);
  const isAdmin = !!authUser?.is_admin;

  const isCreateMode = id === 'create';
  const poId = isCreateMode ? undefined : Number(id);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRecreate, setCancelRecreate] = useState<'0' | '1'>('0');
  const [showRejectCancelModal, setShowRejectCancelModal] = useState(false);
  const [rejectCancelReason, setRejectCancelReason] = useState('');
  const [isQuotationOpen, setIsQuotationOpen] = useState(false);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [isEditDocumentModalOpen, setIsEditDocumentModalOpen] = useState(false);
  const [editDocumentType, setEditDocumentType] = useState<'quotation' | 'invoice' | 'delivery' | 'jobcard' | 'ticket' | 'other' | null>(null);
  const [editDocumentId, setEditDocumentId] = useState<number | null>(null);
  const [expandedDocuments, setExpandedDocuments] = useState<Set<string>>(new Set());

  // GRN Management state
  const [grns, setGrns] = useState<any[]>([]);
  const [grnsLoading, setGrnsLoading] = useState(false);
  const [showCreateGrnModal, setShowCreateGrnModal] = useState(false);
  const [grnPoItems, setGrnPoItems] = useState<any[]>([]);
  const [grnForm, setGrnForm] = useState<{ po_line_id: number; received_qty: string; variance_reason: string }[]>([]);
  const [grnDetail, setGrnDetail] = useState<any>(null);
  const [grnDetailLoading, setGrnDetailLoading] = useState(false);
  const [showGrnDetailModal, setShowGrnDetailModal] = useState(false);
  const [grnMeta, setGrnMeta] = useState({ received_date: '', received_time: '', received_by: '', notes: '' });
  const [grnItemForms, setGrnItemForms] = useState<any[]>([]);

  // Credit Notes state
  const [creditNotes, setCreditNotes] = useState<any[]>([]);
  const [creditNotesLoading, setCreditNotesLoading] = useState(false);
  const [showCreateCreditNoteModal, setShowCreateCreditNoteModal] = useState(false);
  const [newCreditNote, setNewCreditNote] = useState({ credit_note_number: '', credit_note_date: '', reason: '', vat_type: 'inclusive', non_vat: false });
  const [newCreditLineItem, setNewCreditLineItem] = useState({ description: '', quantity: '', total_price: '', vat_type: 'inclusive', non_vat: false });
  const [expandedCreditNoteId, setExpandedCreditNoteId] = useState<number | null>(null);
  const [showEditCreditNoteModal, setShowEditCreditNoteModal] = useState(false);
  const [editingCreditNote, setEditingCreditNote] = useState<any>(null);
  const [editCreditNoteForm, setEditCreditNoteForm] = useState({ credit_note_number: '', credit_note_date: '', delivery_date: '', received_by: '', reason: '', vat_type: 'inclusive', non_vat: false });

  // PO Amendment state
  const [amendments, setAmendments] = useState<any[]>([]);
  const [amendmentsLoading, setAmendmentsLoading] = useState(false);
  const [showCreateAmendmentModal, setShowCreateAmendmentModal] = useState(false);
  const [newAmendment, setNewAmendment] = useState({ justification: '', approved_by: '', invoice_amount: '', notes: '' });

  // Delivery Summary state
  const [deliverySummary, setDeliverySummary] = useState<any>(null);
  const [deliverySummaryLoading, setDeliverySummaryLoading] = useState(false);
  const [showDeliverySummary, setShowDeliverySummary] = useState(false);

  useEffect(() => {
    if (isCreateMode) {
      return;
    }
    if (!poId) return;
    dispatch(fetchPurchaseOrderDetail(poId));
    return () => {
      dispatch(clearCurrentPo());
    };
  }, [dispatch, poId, isCreateMode]);

  // Fetch GRNs, credit notes, amendments when PO is loaded
  useEffect(() => {
    if (!poId) return;
    setGrnsLoading(true);
    dispatch(fetchGrns(poId)).then((res: any) => {
      if (res.payload?.data) setGrns(res.payload.data);
      setGrnsLoading(false);
    });
    setCreditNotesLoading(true);
    dispatch(fetchCreditNotes(poId)).then((res: any) => {
      if (res.payload?.data) setCreditNotes(res.payload.data);
      setCreditNotesLoading(false);
    });
    setAmendmentsLoading(true);
    dispatch(fetchAmendments(poId)).then((res: any) => {
      if (res.payload?.data) setAmendments(res.payload.data);
      setAmendmentsLoading(false);
    });
  }, [dispatch, poId]);

  // GRN handlers
  const handleOpenCreateGrn = async () => {
    if (!poId) return;
    try {
      const res = await dispatch(fetchGrnPoItems(poId)).unwrap();
      const items = res.data || [];
      setGrnPoItems(items);
      setGrnItemForms(items.map((item: any) => ({
        po_line_id: item.id,
        item_code: item.item_code || '',
        description: item.description || item.name || '',
        ordered_qty: item.ordered_qty,
        received_qty: '',
        unit_price: item.unit_price?.toString() || '0',
        vatable: item.vatable ?? true,
        variance_reason: '',
      })));
      const now = new Date();
      setGrnMeta({
        received_date: now.toISOString().split('T')[0],
        received_time: now.toTimeString().slice(0, 5),
        received_by: authUser?.name || '',
        notes: '',
      });
      setShowCreateGrnModal(true);
    } catch (err: any) {
      toast.error(err || 'Failed to load PO items');
    }
  };

  const computeGrnTotals = (items: any[]) => {
    let net = 0, vat = 0;
    items.forEach((it) => {
      const qty = Number(it.received_qty) || 0;
      const price = Number(it.unit_price) || 0;
      const lineTotal = qty * price;
      net += lineTotal;
      if (it.vatable) vat += lineTotal * 0.15;
    });
    return { net, vat, gross: net + vat };
  };

  const handleCreateGrn = async (finalize: boolean = false) => {
    if (!poId) return;
    const items = grnItemForms.filter(f => f.received_qty && Number(f.received_qty) > 0);
    if (items.length === 0) {
      toast.error('Enter at least one received quantity');
      return;
    }
    if (!grnMeta.received_date || !grnMeta.received_by) {
      toast.error('Received date and received by are required');
      return;
    }
    try {
      const payload = {
        received_date: grnMeta.received_date,
        received_time: grnMeta.received_time || '00:00',
        received_by: grnMeta.received_by,
        notes: grnMeta.notes,
        items: items.map(it => ({
          po_line_id: it.po_line_id,
          item_code: it.item_code,
          description: it.description,
          ordered_qty: it.ordered_qty,
          received_qty: Number(it.received_qty),
          unit_price: Number(it.unit_price),
          vatable: it.vatable,
          variance_reason: it.variance_reason || null,
        })),
      };
      const res = await dispatch(createGrn({ poRequestId: poId, data: payload })).unwrap();
      const newGrn = res.data;
      if (finalize && newGrn) {
        await dispatch(finalizeGrn(newGrn.id)).unwrap();
        toast.success('GRN created and finalized');
      } else {
        toast.success('GRN draft saved');
      }
      setShowCreateGrnModal(false);
      const grnsRes = await dispatch(fetchGrns(poId)).unwrap();
      if (grnsRes?.data) setGrns(grnsRes.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err || 'Failed to create GRN');
    }
  };

  const handleViewGrn = async (grnId: number) => {
    setGrnDetailLoading(true);
    setShowGrnDetailModal(true);
    try {
      const res = await dispatch(fetchGrnDetail(grnId)).unwrap();
      setGrnDetail(res.data);
    } catch (err: any) {
      toast.error(err || 'Failed to load GRN details');
    } finally {
      setGrnDetailLoading(false);
    }
  };

  const handleFinalizeGrn = async (grnId: number) => {
    if (!window.confirm('Finalize this GRN? This action cannot be undone.')) return;
    try {
      await dispatch(finalizeGrn(grnId)).unwrap();
      toast.success('GRN finalized');
      if (poId) {
        const res = await dispatch(fetchGrns(poId)).unwrap();
        if (res?.data) setGrns(res.data);
      }
    } catch (err: any) {
      toast.error(err || 'Failed to finalize GRN');
    }
  };

  const handleDeleteGrn = async (grnId: number) => {
    if (!window.confirm('Delete this GRN?')) return;
    try {
      await dispatch(removeGrn(grnId)).unwrap();
      toast.success('GRN deleted');
      setGrns(grns.filter(g => g.id !== grnId));
    } catch (err: any) {
      toast.error(err || 'Failed to delete GRN');
    }
  };

  // Credit Note handlers
  const handleCreateCreditNote = async () => {
    if (!poId) return;
    try {
      const res = await dispatch(createCreditNote({ poId, data: newCreditNote })).unwrap();
      toast.success('Credit note created');
      setCreditNotes([...creditNotes, res.data]);
      setShowCreateCreditNoteModal(false);
      setNewCreditNote({ credit_note_number: '', credit_note_date: '', reason: '', vat_type: 'inclusive', non_vat: false });
    } catch (err: any) {
      toast.error(err || 'Failed to create credit note');
    }
  };

  const handleDeleteCreditNote = async (creditNoteId: number) => {
    if (!poId) return;
    if (!window.confirm('Delete this credit note?')) return;
    try {
      await dispatch(deleteCreditNote({ poId, creditNoteId })).unwrap();
      toast.success('Credit note deleted');
      setCreditNotes(creditNotes.filter(cn => cn.id !== creditNoteId));
    } catch (err: any) {
      toast.error(err || 'Failed to delete credit note');
    }
  };

  const handleAddCreditLineItem = async (creditNoteId: number) => {
    if (!poId || !newCreditLineItem.description) return;
    try {
      const res = await dispatch(addCreditNoteLineItem({ poId, creditNoteId, data: {
        description: newCreditLineItem.description,
        quantity: Number(newCreditLineItem.quantity),
        total_price: Number(newCreditLineItem.total_price),
        vat_type: newCreditLineItem.vat_type,
        non_vat: newCreditLineItem.non_vat,
      } })).unwrap();
      setCreditNotes(creditNotes.map(cn => cn.id === creditNoteId ? { ...cn, line_items: [...(cn.line_items || []), res.data] } : cn));
      setNewCreditLineItem({ description: '', quantity: '', total_price: '', vat_type: 'inclusive', non_vat: false });
      toast.success('Line item added');
    } catch (err: any) {
      toast.error(err || 'Failed to add line item');
    }
  };

  const handleRemoveCreditLineItem = async (creditNoteId: number, lineItemId: number) => {
    if (!poId) return;
    try {
      await dispatch(removeCreditNoteLineItem({ poId, creditNoteId, lineItemId })).unwrap();
      setCreditNotes(creditNotes.map(cn => cn.id === creditNoteId ? { ...cn, line_items: (cn.line_items || []).filter((li: any) => li.id !== lineItemId) } : cn));
      toast.success('Line item removed');
    } catch (err: any) {
      toast.error(err || 'Failed to remove line item');
    }
  };

  const handleOpenEditCreditNote = (cn: any) => {
    setEditingCreditNote(cn);
    setEditCreditNoteForm({
      credit_note_number: cn.credit_note_number || '',
      credit_note_date: cn.credit_note_date || '',
      delivery_date: cn.delivery_date || '',
      received_by: cn.received_by || '',
      reason: cn.reason || '',
      vat_type: cn.vat_type || 'inclusive',
      non_vat: !!cn.non_vat,
    });
    setShowEditCreditNoteModal(true);
  };

  const handleUpdateCreditNote = async () => {
    if (!poId || !editingCreditNote) return;
    try {
      const res = await dispatch(updateCreditNote({ poId, creditNoteId: editingCreditNote.id, data: editCreditNoteForm })).unwrap();
      toast.success('Credit note updated');
      setCreditNotes(creditNotes.map(cn => cn.id === editingCreditNote.id ? { ...cn, ...res.data } : cn));
      setShowEditCreditNoteModal(false);
      setEditingCreditNote(null);
    } catch (err: any) {
      toast.error(err || 'Failed to update credit note');
    }
  };

  // Amendment handlers
  const handleCreateAmendment = async () => {
    if (!poId) return;
    if (!newAmendment.justification || !newAmendment.approved_by) {
      toast.error('Justification and approver are required');
      return;
    }
    try {
      const res = await dispatch(createAmendment({ poId, data: {
        justification: newAmendment.justification,
        approved_by: Number(newAmendment.approved_by),
        invoice_amount: newAmendment.invoice_amount ? Number(newAmendment.invoice_amount) : undefined,
        notes: newAmendment.notes || undefined,
      } })).unwrap();
      toast.success('Amendment created');
      setAmendments([...amendments, res.data]);
      setShowCreateAmendmentModal(false);
      setNewAmendment({ justification: '', approved_by: '', invoice_amount: '', notes: '' });
    } catch (err: any) {
      toast.error(err || 'Failed to create amendment');
    }
  };

  const handleApproveAmendment = async (amendmentId: number) => {
    if (!poId) return;
    try {
      const res = await dispatch(approveAmendment({ poId, amendmentId })).unwrap();
      toast.success('Amendment approved');
      setAmendments(amendments.map(a => a.id === amendmentId ? res.data : a));
    } catch (err: any) {
      toast.error(err || 'Failed to approve amendment');
    }
  };

  const handleRejectAmendment = async (amendmentId: number) => {
    if (!poId) return;
    const reason = window.prompt('Reason for rejection?') || '';
    try {
      const res = await dispatch(rejectAmendment({ poId, amendmentId, reason })).unwrap();
      toast.success('Amendment rejected');
      setAmendments(amendments.map(a => a.id === amendmentId ? res.data : a));
    } catch (err: any) {
      toast.error(err || 'Failed to reject amendment');
    }
  };

  // Delivery Summary handler
  const handleFetchDeliverySummary = async () => {
    if (!poId) return;
    setDeliverySummaryLoading(true);
    try {
      const res = await dispatch(fetchOrderDeliverySummary(poId)).unwrap();
      setDeliverySummary(res.data);
      setShowDeliverySummary(true);
    } catch (err: any) {
      toast.error(err || 'Failed to fetch delivery summary');
    } finally {
      setDeliverySummaryLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!current || !po.can_approve) return;
    try {
      await dispatch(approvePurchaseOrder(po.id)).unwrap();
      toast.success('Purchase order approved successfully');
    } catch (err: any) {
      toast.error(err || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!current || !po.can_reject || !rejectReason.trim()) return;
    try {
      await dispatch(rejectPurchaseOrder({ id: po.id, reason: rejectReason })).unwrap();
      toast.success('Purchase order rejected');
      setShowRejectModal(false);
      setRejectReason('');
    } catch (err: any) {
      toast.error(err || 'Failed to reject');
    }
  };

  const handleSubmit = async () => {
    if (!current || !po.can_submit) return;
    try {
      await dispatch(submitPurchaseOrder(po.id)).unwrap();
      toast.success('Submitted for approval');
    } catch (err: any) {
      toast.error(err || 'Failed to submit');
    }
  };

  const handleUnsubmit = async () => {
    if (!current || !po.can_unsubmit) return;
    try {
      await dispatch(unsubmitPurchaseOrder(po.id)).unwrap();
      toast.success('Unsubmitted');
    } catch (err: any) {
      toast.error(err || 'Failed to unsubmit');
    }
  };

  const handleCancel = () => {
    if (!current || !po.can_cancel) return;
    setCancelReason('');
    setCancelRecreate('0');
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!current || !cancelReason.trim()) return;
    try {
      await dispatch(cancelPurchaseOrder({ id: po.id, reason: cancelReason.trim(), recreate: cancelRecreate === '1' })).unwrap();
      toast.success('Cancellation request sent for approval');
      setShowCancelModal(false);
      dispatch(fetchPurchaseOrderDetail(po.id));
    } catch (err: any) {
      toast.error(err || 'Failed to request cancellation');
    }
  };

  const handleApproveCancel = async () => {
    if (!current || !po.can_approve_cancel) return;
    if (!window.confirm('Approve the cancellation of this purchase order?')) return;
    try {
      await dispatch(approveCancelPurchaseOrder(po.id)).unwrap();
      toast.success('Cancellation approval recorded');
      dispatch(fetchPurchaseOrderDetail(po.id));
    } catch (err: any) {
      toast.error(err || 'Failed to approve cancellation');
    }
  };

  const confirmRejectCancel = async () => {
    if (!current || !rejectCancelReason.trim()) return;
    try {
      await dispatch(rejectCancelPurchaseOrder({ id: po.id, reason: rejectCancelReason.trim() })).unwrap();
      toast.success('Cancellation request rejected');
      setShowRejectCancelModal(false);
      setRejectCancelReason('');
      dispatch(fetchPurchaseOrderDetail(po.id));
    } catch (err: any) {
      toast.error(err || 'Failed to reject cancellation');
    }
  };

  const handleRecreate = async () => {
    if (!current || !po.can_recreate) return;
    if (!window.confirm('Recreate this cancelled order as a new pending order?')) return;
    try {
      await dispatch(recreatePurchaseOrder(po.id)).unwrap();
      toast.success('Order recreated as a new pending order');
    } catch (err: any) {
      toast.error(err || 'Failed to recreate order');
    }
  };

  const handleDocumentUploaded = async (updatedPo: PoDetail) => {
    // Refresh the PO data to show updated documents and history
    await dispatch(fetchPurchaseOrderDetail(Number(id))).unwrap();
    setIsAddDocumentModalOpen(false);
  };

  const handleDeleteDocument = async (type: string, documentId: number) => {
    if (!!current?.is_fulfilled && !isAdmin) {
      toast.error('Cannot delete documents on a fulfilled purchase order.');
      return;
    }
    if (type === 'quotation' && !!current?.order_number && current?.status_id === 2) {
      toast.error('Cannot remove quotation from an approved order with an order number.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await deletePoDocumentApi(Number(id), type, documentId);
      toast.success('Document deleted successfully');
      dispatch(fetchPurchaseOrderDetail(Number(id)));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete document');
    }
  };

  const handleEditDocument = (type: 'quotation' | 'invoice' | 'delivery' | 'jobcard' | 'ticket' | 'other', documentId: number) => {
    if (!!current?.is_fulfilled && !isAdmin) {
      toast.error('Cannot edit documents on a fulfilled purchase order.');
      return;
    }
    setEditDocumentType(type);
    setEditDocumentId(documentId);
    setIsEditDocumentModalOpen(true);
  };

  const handleDocumentUpdated = (updatedPo: PoDetail) => {
    dispatch(fetchPurchaseOrderDetail(Number(id)));
    setIsEditDocumentModalOpen(false);
  };

  const toggleDocumentExpansion = (docId: string) => {
    setExpandedDocuments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const handleDelete = async () => {
    if (!current || !po.can_delete) return;
    if (!window.confirm('Delete this purchase order? This cannot be undone.')) return;
    try {
      await dispatch(deletePurchaseOrder(po.id)).unwrap();
      toast.success('Purchase order deleted');
      navigate('/purchase-orders');
    } catch (err: any) {
      toast.error(err || 'Failed to delete');
    }
  };

  if (detailLoading) {
    return <div className="text-center p-10 dark:text-gray-300">Loading purchase order details...</div>;
  }

  if (!current && !isCreateMode) {
    return (
      <div className="text-center p-10 dark:text-gray-300">
        Purchase order not found or ID is missing.
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Go Back
        </button>
      </div>
    );
  }

  if (isCreateMode) {
    return (
      <div className="container mx-auto p-4 md:p-6 lg:p-8 max-w-5xl">
        <button
          onClick={() => navigate('/purchase-orders')}
          className="mb-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          &larr; Back to List
        </button>
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden p-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Create Purchase Order</h2>
          <p className="text-gray-600 dark:text-gray-400">Create PO functionality is not yet implemented. Please use the Livewire interface to create purchase orders.</p>
        </div>
      </div>
    );
  }

  // At this point, current is guaranteed to be non-null due to early returns above
  const po = current!;
  const isFulfilled = !!po.is_fulfilled;
  const canModifyDocuments = !isFulfilled || isAdmin;

  const statusId = po.status_id;
  const submitted = typeof po.submitted === 'number' ? po.submitted !== 0 : po.submitted;
  let statusText = 'Pending';
  let statusColor = 'bg-yellow-400 text-yellow-900';
  if (statusId === 2) { statusText = 'Approved'; statusColor = 'bg-green-500 text-white'; }
  else if (statusId === 3) { statusText = 'Rejected'; statusColor = 'bg-red-500 text-white'; }
  else if (statusId === 4) { statusText = 'Cancelled'; statusColor = 'bg-orange-500 text-white'; }
  else if (submitted) { statusText = 'Submitted'; statusColor = 'bg-blue-400 text-blue-900'; }
  else if (po.reference_number && !po.order_number) { statusText = 'TBA'; statusColor = 'bg-purple-400 text-purple-900'; }

  let borderAccent = 'border-gray-500';
  if (statusId === 2) borderAccent = 'border-green-500';
  else if (statusId === 3) borderAccent = 'border-red-500';
  else if (statusId === 4) borderAccent = 'border-orange-500';
  else if (submitted) borderAccent = 'border-blue-500';
  else if (po.reference_number && !po.order_number) borderAccent = 'border-purple-500';

  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8 max-w-8xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        &larr; Back to List
      </button>

      {error && (
        <div className="my-4 p-3 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-md shadow text-center">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden">
        {/* Header */}
        <div className={`p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 border-l-4 ${borderAccent}`}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                {po.order_number ? `PO #${po.order_number}` : `PO #${po.id}`}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Date: {formatDate(po.date)}
                {!!po.reference_number && ` | Ref: ${po.reference_number}`}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2 sm:mt-0 flex-wrap">
              {!!po.is_linked && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                  Linked Order
                </span>
              )}
              {!!(po.internal === 1) && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300">
                  Internal
                </span>
              )}
              {!!po.can_submit && (
                <button
                  onClick={handleSubmit}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50"
                >
                  Submit
                </button>
              )}
              {!!po.can_edit && (!isFulfilled || isAdmin) && (
                <button
                  onClick={() => navigate(`/purchase-orders/${po.id}/edit`)}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                >
                  Edit
                </button>
              )}
              {!!po.can_unsubmit && (
                <button
                  onClick={handleUnsubmit}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 transition-colors disabled:opacity-50"
                >
                  Unsubmit
                </button>
              )}
              {!!po.can_cancel && (
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel Order
                </button>
              )}
              {!!po.can_approve_cancel && (
                <button
                  onClick={handleApproveCancel}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50"
                >
                  Approve Cancellation
                </button>
              )}
              {!!po.can_reject_cancel && (
                <button
                  onClick={() => { setRejectCancelReason(''); setShowRejectCancelModal(true); }}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-orange-500 hover:bg-orange-600 text-white transition-colors disabled:opacity-50"
                >
                  Reject Cancellation
                </button>
              )}
              {!!po.can_recreate && (
                <button
                  onClick={handleRecreate}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50"
                >
                  Recreate Order
                </button>
              )}
              {!!po.can_delete && (!isFulfilled || isAdmin) && (
                <button
                  onClick={handleDelete}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                >
                  Delete
                </button>
              )}
              <span className={`px-3 py-1 text-sm font-semibold rounded-full ${statusColor}`}>
                {statusText}
              </span>
              {isFulfilled && (
                <span className="px-3 py-1 text-sm font-semibold rounded-full bg-emerald-500 text-white">
                  Fulfilled
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Approve/Reject action bar */}
        {(!!po.can_approve || !!po.can_reject) && (
          <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex flex-col sm:flex-row gap-3">
            {!!po.can_approve && (
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className={`w-full sm:w-auto text-white font-bold py-2 px-4 rounded transition-colors duration-150 ease-in-out flex items-center justify-center bg-green-500 hover:bg-green-600 shadow-sm ${actionLoading ? 'opacity-70 cursor-wait' : ''}`}
              >
                {actionLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Approving...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    Approve
                  </>
                )}
              </button>
            )}
            {!!po.can_reject && (
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
                className={`w-full sm:w-auto text-white font-bold py-2 px-4 rounded transition-colors duration-150 ease-in-out flex items-center justify-center bg-red-500 hover:bg-red-600 shadow-sm ${actionLoading ? 'opacity-70 cursor-wait' : ''}`}
              >
                {actionLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                    </svg>
                    Reject
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Main content */}
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 gap-x-6 gap-y-4 md:gap-y-6">
            {/* Order Information — left column */}
            <div className="md:col-span-1 lg:col-span-1 space-y-3">
              <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-2 dark:border-gray-600">Order Information</h4>
              <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm">
                <ul className="space-y-2">
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Date:</span> {formatDate(po.date)}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Requested by:</span> {po.user?.name || 'N/A'}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      Store{(po.stores?.length || 0) > 1 ? `s (${po.stores!.length})` : ''}:
                    </span>{' '}
                    {(po.stores?.length || 0) <= STORE_PREVIEW_LIMIT ? (
                      <>{po.stores?.map((s) => s.name).join(', ') || 'N/A'}</>
                    ) : (
                      <span title={po.stores!.map((s) => s.name).join(', ')}>
                        {po.stores!.slice(0, STORE_PREVIEW_LIMIT).map((s) => s.name).join(', ')}
                        {' '}+{po.stores!.length - STORE_PREVIEW_LIMIT} more
                      </span>
                    )}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {po.internal === 1 ? 'Technician:' : 'Supplier:'}
                    </span>{' '}
                    {po.internal === 1 ? (po.technician?.name || 'N/A') : (po.supplier?.name || 'N/A')}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Fulfillment:</span> {po.fulfillment?.name || 'N/A'}
                  </li>
                  {!!po.category && (
                    <li className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Category:</span> {po.category.name}{po.subCategory && ` - ${po.subCategory.name}`}
                    </li>
                  )}
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Internal:</span> {po.internal === 1 ? 'Yes' : 'No'}
                  </li>
                </ul>
              </div>
            </div>

            {/* Approval Chain — right column */}
            <div className="md:col-span-2 lg:col-span-1 space-y-3">
              <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-2 dark:border-gray-600">Approval Chain</h4>
              {po.approvers && po.approvers.length > 0 ? (
                <ul className="space-y-2">
                  {po.approvers.map((approver, index) => {
                    let approverStatus = 'Pending';
                    let bgColorClass = 'bg-gray-600 text-white dark:bg-gray-600 dark:text-white';
                    if (approver.status_id === 2) { approverStatus = 'Approved'; bgColorClass = 'bg-green-600 text-white dark:bg-green-600 dark:text-white'; }
                    else if (approver.status_id === 3) { approverStatus = 'Rejected'; bgColorClass = 'bg-red-600 text-white dark:bg-red-600 dark:text-white'; }
                    const borderColor = approver.status_id === 1 ? 'border-blue-200 dark:border-blue-700'
                      : approver.status_id === 2 ? 'border-green-200 dark:border-green-700'
                      : approver.status_id === 3 ? 'border-red-200 dark:border-red-700'
                      : 'border-gray-200 dark:border-gray-600';
                    return (
                      <li key={approver.id || index} className={`p-2.5 border rounded-md shadow-sm transition-colors ${bgColorClass} ${borderColor}`}>
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-medium text-sm text-white">{approver.user?.name || 'Unknown'}</span>
                            {!!approver.tba && (
                              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-white bg-opacity-10 text-white">TBA</span>
                            )}
                            {approver.status_id === 3 && approver.comment && (
                              <p className="text-xs text-red-200 dark:text-red-100 mt-0.5">Reason: {approver.comment}</p>
                            )}
                          </div>
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-white bg-opacity-10 text-white">
                            {approverStatus}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No approvers assigned.</p>
              )}
            </div>

            {/* Full-width sections */}
            <div className="md:col-span-3 lg:col-span-2 space-y-4">
              {/* Linked Orders */}
              {po.is_linked_order && po.linked_family && po.linked_family.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Linked Orders</h4>
                  <div className="space-y-2">
                    {po.linked_family.map((linked) => (
                      <Link
                        key={linked.id}
                        to={`/purchase-orders/${linked.id}`}
                        className="block p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{linked.order_number || ''}</span>
                            {linked.id === po.id && (
                              <span className="ml-2 text-xs text-blue-500">(this order)</span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {linked.stores?.map((s) => s.name).join(', ')}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* PO Items - matching requisitions details page format */}
              {((po.equipment_items && po.equipment_items.length > 0) || (po.asset_description_pairs && po.asset_description_pairs.length > 0) || (po.assets && po.assets.length > 0) || (po.equipmentRequests && po.equipmentRequests.length > 0) || (po.items && po.items.length > 0)) && (
                <div>
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Items to Repair / Request</h4>
                  <div className="space-y-3">
                    {(() => {
                      const items: JSX.Element[] = [];

                      // Equipment items from Laravel (matches Livewire exactly)
                      if (po.equipment_items && po.equipment_items.length > 0) {
                        po.equipment_items.forEach((item: any, index: number) => {
                          items.push(
                            <div
                              key={`processed-eq-${index}`}
                              className="p-2.5 border border-amber-200 dark:border-amber-700 rounded-md bg-amber-100 dark:bg-amber-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  <strong>{item.description}</strong>
                                  {(item.category || item.subcategory) && (
                                    <><br />
                                      <small>Category: {item.category} {item.subcategory ? `Subcategory: ${item.subcategory}` : ''}</small>
                                    </>
                                  )}
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                  Equipment
                                </span>
                              </div>
                              <div className="mt-1 border-t border-amber-100 dark:border-amber-800 pt-1">
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  <span className="font-medium">Serial Number:</span> {item.serial_number || 'N/A'}
                                </p>
                                {item.repair_notes && (
                                  <div className="mt-1">
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Repair Notes:</span>
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line">{item.repair_notes}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      }

                      // Asset description pairs (use processed data first, then fall back to raw assets)
                      if (po.asset_description_pairs && po.asset_description_pairs.length > 0) {
                        po.asset_description_pairs.forEach((pair: any, index: number) => {
                          items.push(
                            <div
                              key={`processed-asset-${index}`}
                              className="p-2.5 border border-indigo-200 dark:border-indigo-700 rounded-md bg-indigo-100 dark:bg-indigo-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  Asset: {pair.asset}
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
                                  Asset
                                </span>
                              </div>
                              <div className="mt-1 border-t border-indigo-100 dark:border-indigo-800 pt-1">
                                {pair.descriptions && pair.descriptions.length > 0 ? (
                                  <div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Description:</span>
                                    </p>
                                    <ul className="list-disc list-inside ml-2 text-xs text-gray-600 dark:text-gray-400">
                                      {pair.descriptions.map((desc: any, dIndex: number) => {
                                        const descText = typeof desc === 'string' ? desc : desc.description || '';
                                        return descText && <li key={dIndex}>{descText}</li>;
                                      })}
                                    </ul>
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">Description:</span> <em>No description</em>
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        });
                      } else if (po.assets && po.assets.length > 0) {
                        // Fallback to raw assets if processed pairs not available
                        po.assets.forEach((asset: any, index: number) => {
                          items.push(
                            <div
                              key={`raw-asset-${index}`}
                              className="p-2.5 border border-indigo-200 dark:border-indigo-700 rounded-md bg-indigo-100 dark:bg-indigo-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  Asset: {asset.asset || 'N/A'}
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
                                  Asset
                                </span>
                              </div>
                              <div className="mt-1 border-t border-indigo-100 dark:border-indigo-800 pt-1">
                                {asset.descriptions && asset.descriptions.length > 0 ? (
                                  <div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Description:</span>
                                    </p>
                                    <ul className="list-disc list-inside ml-2 text-xs text-gray-600 dark:text-gray-400">
                                      {asset.descriptions.map((desc: any, dIndex: number) => {
                                        const descText = typeof desc === 'string' ? desc : desc.description || '';
                                        return descText && <li key={dIndex}>{descText}</li>;
                                      })}
                                    </ul>
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">Description:</span> <em>No description</em>
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        });
                      }

                      // Fallback to legacy equipment requests
                      if (items.length === 0 && po.equipmentRequests && po.equipmentRequests.length > 0) {
                        po.equipmentRequests.forEach((item: any, index: number) => {
                          items.push(
                            <div
                              key={`eq-${item.id || index}`}
                              className="p-2.5 border border-amber-200 dark:border-amber-700 rounded-md bg-amber-100 dark:bg-amber-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  {item.equipment?.description || 'Equipment Item'}
                                  {item.equipment?.serial_number && (
                                    <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                                      (S/N: {item.equipment.serial_number})
                                    </span>
                                  )}
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                  Equipment
                                </span>
                              </div>
                            </div>
                          );
                        });
                      }

                      // Fallback to simple items table if no structured data
                      if (items.length === 0 && po.items && po.items.length > 0) {
                        return (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-600">
                                  <th className="py-2 pr-4">Description</th>
                                  <th className="py-2 pr-4">Qty</th>
                                  <th className="py-2 pr-4">Unit</th>
                                  <th className="py-2 pr-4">Price</th>
                                  <th className="py-2">VAT</th>
                                </tr>
                              </thead>
                              <tbody>
                                {po.items.map((item) => (
                                  <tr key={item.id} className="border-b border-gray-100 dark:border-gray-700">
                                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{item.description}</td>
                                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{item.quantity}</td>
                                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{item.unit_of_measure || 'EA'}</td>
                                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{formatCurrency(item.price)}</td>
                                    <td className="py-2 text-gray-700 dark:text-gray-300">{item.non_vat ? 'Exempt' : (item.vat || 'Inclusive')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      }

                      return items.length > 0 ? items : null;
                    })()}
                  </div>
                </div>
              )}

              {/* Quotation */}
              {po.quotation ? (
                <div className="mt-6">
                  <div className="p-3 border border-green-200 dark:border-green-700 rounded-md bg-green-50 dark:bg-green-900/70 shadow-sm">
                    <button
                      type="button"
                      className="flex items-center w-full justify-between text-left text-green-700 dark:text-green-200 font-semibold focus:outline-none"
                      onClick={() => setIsQuotationOpen((prev) => !prev)}
                    >
                      <div className="flex flex-wrap gap-x-6 gap-y-1 items-center w-full">
                        {po.quotation.quotation_number && (
                          <span className="text-sm font-medium">Quotation #: <span className="font-semibold">{po.quotation.quotation_number}</span></span>
                        )}
                        {po.quotation.date && (
                          <span className="text-sm font-medium">Date: <span className="font-normal">{formatDate(po.quotation.date)}</span></span>
                        )}
                        <span className="text-sm font-medium">Amount: <span className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(po.quotation.amount)}</span></span>
                        {po.quotation.vat !== undefined && (
                          <span className="text-sm font-medium">VAT: <span className="font-normal">{po.quotation.vat === 1 ? 'Inclusive' : 'Exclusive'}</span></span>
                        )}
                      </div>
                      <svg
                        className={`w-5 h-5 ml-2 transition-transform ${isQuotationOpen ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    {isQuotationOpen && (
                      <div className="mt-3 space-y-3">
                        {po.quotation.attachment && (
                          <div className="flex justify-end gap-2">
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Quotes/${po.quotation.attachment}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              View Quote
                            </a>
                            {canModifyDocuments && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => po.quotation?.id && handleEditDocument('quotation', po.quotation.id)}
                                  className="inline-flex items-center px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                                >
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => po.quotation?.id && handleDeleteDocument('quotation', po.quotation.id)}
                                  className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                >
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        {po.quotation.attachment && (
                          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                            <iframe
                              src={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Quotes/${po.quotation.attachment}`}
                              className="w-full h-96"
                              title="Quote Document"
                              onError={(e) => {
                                (e.target as HTMLIFrameElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Invoices */}
              {po.invoices && po.invoices.length > 0 && (
                <div className="mt-4">
                  <div className="flex justify-between items-center border-b pb-1 mb-3 dark:border-gray-600">
                    <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300">Invoices</h4>
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                      Total: {formatCurrency(po.invoices.reduce((sum: number, inv) => sum + (typeof inv.amount === 'string' ? parseFloat(inv.amount) : (inv.amount || 0)), 0))}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {po.invoices.map((inv) => {
                      const docId = `invoice-${inv.id}`;
                      const isExpanded = expandedDocuments.has(docId);
                      return (
                        <div key={inv.id} className="p-3 border border-blue-200 dark:border-blue-700 rounded-md bg-blue-50 dark:bg-blue-900/70 shadow-sm">
                          <button
                            type="button"
                            className="flex items-center w-full justify-between text-left text-blue-700 dark:text-blue-200 font-semibold focus:outline-none"
                            onClick={() => toggleDocumentExpansion(docId)}
                          >
                            <div className="flex flex-wrap gap-x-6 gap-y-1 items-center w-full">
                              {inv.invoice_number && (
                                <span className="text-sm font-medium">Invoice #: <span className="font-semibold">{inv.invoice_number}</span></span>
                              )}
                              {inv.date && (
                                <span className="text-sm font-medium">Date: <span className="font-normal">{formatDate(inv.date)}</span></span>
                              )}
                              {inv.amount && (
                                <span className="text-sm font-medium">Amount: <span className="font-bold text-blue-600 dark:text-blue-400">{formatCurrency(typeof inv.amount === 'string' ? parseFloat(inv.amount) : inv.amount)}</span></span>
                              )}
                              {inv.vat !== undefined && (
                                <span className="text-sm font-medium">VAT: <span className="font-normal">{inv.vat === 1 ? 'Inclusive' : 'Exclusive'}</span></span>
                              )}
                            </div>
                            <svg
                              className={`w-5 h-5 ml-2 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="mt-3 space-y-3">
                              {inv.attachment && (
                                <div className="flex justify-end gap-2">
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Invoices/${inv.attachment}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                  >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    View Invoice
                                  </a>
                                  {canModifyDocuments && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleEditDocument('invoice', inv.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteDocument('invoice', inv.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                              {inv.attachment && (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                  <iframe
                                    src={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Invoices/${inv.attachment}`}
                                    className="w-full h-96"
                                    title="Invoice Document"
                                    onError={(e) => {
                                      (e.target as HTMLIFrameElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Delivery Notes */}
              {po.deliverynotes && po.deliverynotes.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Delivery Notes</h4>
                  <div className="space-y-2">
                    {po.deliverynotes.map((dn) => {
                      const docId = `delivery-${dn.id}`;
                      const isExpanded = expandedDocuments.has(docId);
                      return (
                        <div key={dn.id} className="p-3 border border-orange-200 dark:border-orange-700 rounded-md bg-orange-50 dark:bg-orange-900/70 shadow-sm">
                          <button
                            type="button"
                            className="flex items-center w-full justify-between text-left text-orange-700 dark:text-orange-200 font-semibold focus:outline-none"
                            onClick={() => toggleDocumentExpansion(docId)}
                          >
                            <div className="flex flex-wrap gap-x-6 gap-y-1 items-center w-full">
                              {dn.delivery_number && (
                                <span className="text-sm font-medium">Delivery #: <span className="font-semibold">{dn.delivery_number}</span></span>
                              )}
                              {dn.date && (
                                <span className="text-sm font-medium">Date: <span className="font-normal">{formatDate(dn.date)}</span></span>
                              )}
                              {dn.received_by && (
                                <span className="text-sm font-medium">Received By: <span className="font-normal">{dn.received_by}</span></span>
                              )}
                            </div>
                            <svg
                              className={`w-5 h-5 ml-2 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="mt-3 space-y-3">
                              {dn.attachment && (
                                <div className="flex justify-end gap-2">
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/DeliveryNotes/${dn.attachment}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                  >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    View Delivery Note
                                  </a>
                                  {canModifyDocuments && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleEditDocument('delivery', dn.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteDocument('delivery', dn.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                              {dn.attachment && (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                  <iframe
                                    src={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/DeliveryNotes/${dn.attachment}`}
                                    className="w-full h-96"
                                    title="Delivery Note Document"
                                    onError={(e) => {
                                      (e.target as HTMLIFrameElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Jobcards */}
              {po.jobcards && po.jobcards.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Jobcards</h4>
                  <div className="space-y-2">
                    {po.jobcards.map((jc) => {
                      const docId = `jobcard-${jc.id}`;
                      const isExpanded = expandedDocuments.has(docId);
                      return (
                        <div key={jc.id} className="p-3 border border-purple-200 dark:border-purple-700 rounded-md bg-purple-50 dark:bg-purple-900/70 shadow-sm">
                          <button
                            type="button"
                            className="flex items-center w-full justify-between text-left text-purple-700 dark:text-purple-200 font-semibold focus:outline-none"
                            onClick={() => toggleDocumentExpansion(docId)}
                          >
                            <div className="flex flex-wrap gap-x-6 gap-y-1 items-center w-full">
                              {jc.job_number && (
                                <span className="text-sm font-medium">Job #: <span className="font-semibold">{jc.job_number}</span></span>
                              )}
                            </div>
                            <svg
                              className={`w-5 h-5 ml-2 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="mt-3 space-y-3">
                              {jc.attachment && (
                                <div className="flex justify-end gap-2">
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Jobcards/${jc.attachment}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                  >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    View Jobcard
                                  </a>
                                  {canModifyDocuments && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleEditDocument('jobcard', jc.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteDocument('jobcard', jc.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                              {jc.attachment && (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                  <iframe
                                    src={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Jobcards/${jc.attachment}`}
                                    className="w-full h-96"
                                    title="Jobcard Document"
                                    onError={(e) => {
                                      (e.target as HTMLIFrameElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tickets */}
              {po.tickets && po.tickets.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Colefax Tickets</h4>
                  <div className="space-y-2">
                    {po.tickets.map((ticket) => {
                      const docId = `ticket-${ticket.id}`;
                      const isExpanded = expandedDocuments.has(docId);
                      return (
                        <div key={ticket.id} className="p-3 border border-pink-200 dark:border-pink-700 rounded-md bg-pink-50 dark:bg-pink-900/70 shadow-sm">
                          <button
                            type="button"
                            className="flex items-center w-full justify-between text-left text-pink-700 dark:text-pink-200 font-semibold focus:outline-none"
                            onClick={() => toggleDocumentExpansion(docId)}
                          >
                            <div className="flex flex-wrap gap-x-6 gap-y-1 items-center w-full">
                              {ticket.ticket_number && (
                                <span className="text-sm font-medium">Ticket #: <span className="font-semibold">{ticket.ticket_number}</span></span>
                              )}
                              {ticket.comment && (
                                <span className="text-sm text-gray-600 dark:text-gray-400">{ticket.comment}</span>
                              )}
                            </div>
                            <svg
                              className={`w-5 h-5 ml-2 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="mt-3 space-y-3">
                              {ticket.attachment && (
                                <div className="flex justify-end gap-2">
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Tickets/${ticket.attachment}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                  >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    View Ticket
                                  </a>
                                  {canModifyDocuments && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleEditDocument('ticket', ticket.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteDocument('ticket', ticket.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                              {ticket.attachment && (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                  <iframe
                                    src={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Tickets/${ticket.attachment}`}
                                    className="w-full h-96"
                                    title="Ticket Document"
                                    onError={(e) => {
                                      (e.target as HTMLIFrameElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Other Documents */}
              {po.others && po.others.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Other Documents</h4>
                  <div className="space-y-2">
                    {po.others.map((other) => {
                      const docId = `other-${other.id}`;
                      const isExpanded = expandedDocuments.has(docId);
                      return (
                        <div key={other.id} className="p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900/70 shadow-sm">
                          <button
                            type="button"
                            className="flex items-center w-full justify-between text-left text-gray-700 dark:text-gray-200 font-semibold focus:outline-none"
                            onClick={() => toggleDocumentExpansion(docId)}
                          >
                            <div className="flex flex-wrap gap-x-6 gap-y-1 items-center w-full">
                              {other.title && (
                                <span className="text-sm font-medium"><span className="font-semibold">{other.title}</span></span>
                              )}
                              {other.description && (
                                <span className="text-sm text-gray-600 dark:text-gray-400">{other.description}</span>
                              )}
                            </div>
                            <svg
                              className={`w-5 h-5 ml-2 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="mt-3 space-y-3">
                              {other.attachment && (
                                <div className="flex justify-end gap-2">
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Others/${other.attachment}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                  >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    View Document
                                  </a>
                                  {canModifyDocuments && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleEditDocument('other', other.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteDocument('other', other.id)}
                                        className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                      >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                              {other.attachment && (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                  <iframe
                                    src={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Other/${other.attachment}`}
                                    className="w-full h-96"
                                    title="Other Document"
                                    onError={(e) => {
                                      (e.target as HTMLIFrameElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add Document Button */}
              {canModifyDocuments && (
                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsAddDocumentModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Document
                  </button>
                </div>
              )}

              

              {/* GRN Management */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 dark:border-gray-600">GRNs ({grns.length})</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={handleFetchDeliverySummary}
                      disabled={deliverySummaryLoading}
                      className="px-3 py-1.5 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-50"
                    >
                      {deliverySummaryLoading ? 'Loading...' : 'Delivery Summary'}
                    </button>
                    {po.status_id === 2 && (
                      <button
                        onClick={handleOpenCreateGrn}
                        className="px-3 py-1.5 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                      >
                        Create GRN
                      </button>
                    )}
                  </div>
                </div>

                {grnsLoading ? (
                  <p className="text-sm text-gray-400">Loading GRNs...</p>
                ) : grns.length === 0 ? (
                  <p className="text-sm text-gray-400">No GRNs created yet.</p>
                ) : (
                  <div className="space-y-2">
                    {grns.map((grn) => (
                      <div
                        key={grn.id}
                        onClick={() => handleViewGrn(grn.id)}
                        className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm cursor-pointer hover:border-blue-400 hover:shadow-md transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            <span className="font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">{grn.grn_number || `GRN #${grn.id}`}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${grn.status === 'finalized' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                              {grn.status}
                            </span>
                            {grn.items_count != null && (
                              <span className="text-xs text-gray-400">{grn.items_count} item(s)</span>
                            )}
                            {grn.net_total != null && (
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">R{Number(grn.net_total).toFixed(2)}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            {grn.created_at && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">{formatDateTime(grn.created_at)}</span>
                            )}
                            {grn.status === 'draft' && (
                              <>
                                <button onClick={() => handleFinalizeGrn(grn.id)} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Finalize</button>
                                <button onClick={() => handleDeleteGrn(grn.id)} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Delete</button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Delivery Summary (collapsible) */}
              {showDeliverySummary && deliverySummary && (
                <div className="mt-4 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-md font-semibold text-indigo-700 dark:text-indigo-300">Order Delivery Summary</h4>
                    <button onClick={() => setShowDeliverySummary(false)} className="text-indigo-400 hover:text-indigo-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{deliverySummary.ordered_items_count}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Items Ordered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">{deliverySummary.delivered_items_count}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Items Delivered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{deliverySummary.total_ordered_quantity}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Total Ordered Qty</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{deliverySummary.total_delivered_quantity}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Total Delivered Qty</div>
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Delivery Progress</span>
                      <span className="font-medium text-indigo-700 dark:text-indigo-300">{deliverySummary.delivery_percentage}%</span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all" style={{ width: `${Math.min(deliverySummary.delivery_percentage, 100)}%` }} />
                    </div>
                  </div>
                  {deliverySummary.item_delivery && deliverySummary.item_delivery.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                            <th className="py-1 pr-2">Item</th>
                            <th className="py-1 pr-2 text-right">Ordered</th>
                            <th className="py-1 pr-2 text-right">Delivered</th>
                            <th className="py-1 pr-2 text-right">Remaining</th>
                            <th className="py-1">Progress</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliverySummary.item_delivery.map((item: any) => (
                            <tr key={item.id} className="border-b border-gray-100 dark:border-gray-700/50">
                              <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-300">{item.name || item.item_code || `#${item.id}`}</td>
                              <td className="py-1.5 pr-2 text-right text-gray-600 dark:text-gray-400">{item.ordered_qty}</td>
                              <td className="py-1.5 pr-2 text-right text-green-600 dark:text-green-400">{item.delivered_qty}</td>
                              <td className="py-1.5 pr-2 text-right text-orange-600 dark:text-orange-400">{item.remaining_qty}</td>
                              <td className="py-1.5">
                                <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${item.delivery_percentage >= 100 ? 'bg-green-500' : item.delivery_percentage > 0 ? 'bg-yellow-500' : 'bg-gray-400'}`} style={{ width: `${Math.min(item.delivery_percentage, 100)}%` }} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Credit Notes */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 dark:border-gray-600">Credit Notes ({creditNotes.length})</h4>
                  {po.status_id === 2 && (
                    <button
                      onClick={() => setShowCreateCreditNoteModal(true)}
                      className="px-3 py-1.5 text-xs bg-purple-500 text-white rounded hover:bg-purple-600"
                    >
                      Add Credit Note
                    </button>
                  )}
                </div>
                {creditNotesLoading ? (
                  <p className="text-sm text-gray-400">Loading credit notes...</p>
                ) : creditNotes.length === 0 ? (
                  <p className="text-sm text-gray-400">No credit notes.</p>
                ) : (
                  <div className="space-y-2">
                    {creditNotes.map((cn) => {
                      const cnStatusConfig: Record<number, { label: string; color: string }> = {
                        1: { label: 'Draft', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-400' },
                        2: { label: 'Finalized', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
                        3: { label: 'Cancelled', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
                      };
                      const cnStatus = cnStatusConfig[cn.status_id] || cnStatusConfig[1];
                      return (
                      <div key={cn.id} className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-700 dark:text-gray-300">{cn.credit_note_number || `CN #${cn.id}`}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${cnStatus.color}`}>{cnStatus.label}</span>
                            {cn.credit_note_date && <span className="text-xs text-gray-400">{formatDate(cn.credit_note_date)}</span>}
                            <span className="text-xs font-medium text-purple-600 dark:text-purple-400">{formatCurrency(cn.total_amount)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setExpandedCreditNoteId(expandedCreditNoteId === cn.id ? null : cn.id)}
                              className="text-xs text-blue-500 hover:underline"
                            >
                              {expandedCreditNoteId === cn.id ? 'Hide' : 'View'} Items ({cn.line_items?.length || 0})
                            </button>
                            <button onClick={() => handleOpenEditCreditNote(cn)} className="text-xs text-indigo-500 hover:text-indigo-700">Edit</button>
                            <button onClick={() => handleDeleteCreditNote(cn.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                          </div>
                        </div>
                        {cn.reason && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{cn.reason}</p>}
                        {cn.delivery_date && <p className="text-xs text-gray-400 mt-0.5">Delivery: {formatDate(cn.delivery_date)}</p>}
                        {expandedCreditNoteId === cn.id && (
                          <div className="mt-3 space-y-1">
                            {(cn.line_items || []).map((li: any) => (
                              <div key={li.id} className="flex items-center justify-between text-xs p-2 rounded bg-gray-50 dark:bg-gray-700/30">
                                <div>
                                  <span className="text-gray-700 dark:text-gray-300">{li.description}</span>
                                  <span className="ml-2 text-gray-400">Qty: {li.quantity} @ {formatCurrency(li.unit_price || (li.total_price / (li.quantity || 1)))}</span>
                                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${li.vat_type === 'inclusive' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>{li.vat_type}</span>
                                  {li.non_vat && <span className="ml-1 text-xs text-red-500">Non-VAT</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(li.total_amount || li.total_price)}</span>
                                  <button onClick={() => handleRemoveCreditLineItem(cn.id, li.id)} className="text-red-400 hover:text-red-600">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                  </button>
                                </div>
                              </div>
                            ))}
                            {/* Add line item form */}
                            <div className="mt-2 p-2 rounded border border-dashed border-gray-300 dark:border-gray-600">
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                <input type="text" placeholder="Description" value={newCreditLineItem.description} onChange={(e) => setNewCreditLineItem({ ...newCreditLineItem, description: e.target.value })} className="px-2 py-1 text-xs rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100" />
                                <input type="number" placeholder="Qty" value={newCreditLineItem.quantity} onChange={(e) => setNewCreditLineItem({ ...newCreditLineItem, quantity: e.target.value })} className="px-2 py-1 text-xs rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100" />
                                <input type="number" placeholder="Total Price" value={newCreditLineItem.total_price} onChange={(e) => setNewCreditLineItem({ ...newCreditLineItem, total_price: e.target.value })} className="px-2 py-1 text-xs rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100" />
                                <button onClick={() => handleAddCreditLineItem(cn.id)} disabled={!newCreditLineItem.description} className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50">Add</button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* PO Amendments */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 dark:border-gray-600">Amendments ({amendments.length})</h4>
                  {po.status_id === 2 && (
                    <button
                      onClick={() => setShowCreateAmendmentModal(true)}
                      className="px-3 py-1.5 text-xs bg-amber-500 text-white rounded hover:bg-amber-600"
                    >
                      Request Amendment
                    </button>
                  )}
                </div>
                {amendmentsLoading ? (
                  <p className="text-sm text-gray-400">Loading amendments...</p>
                ) : amendments.length === 0 ? (
                  <p className="text-sm text-gray-400">No amendments.</p>
                ) : (
                  <div className="space-y-2">
                    {amendments.map((am) => (
                      <div key={am.id} className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Amendment #{am.id}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${am.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : am.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                              {am.status}
                            </span>
                            {am.original_total != null && am.amended_total != null && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatCurrency(am.original_total)} → {formatCurrency(am.amended_total)}
                              </span>
                            )}
                            {am.invoice_amount != null && (
                              <span className="text-xs text-blue-600 dark:text-blue-400">Invoice: {formatCurrency(am.invoice_amount)}</span>
                            )}
                          </div>
                          {am.status === 'pending' && (
                            <div className="flex gap-1">
                              <button onClick={() => handleApproveAmendment(am.id)} className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600">Approve</button>
                              <button onClick={() => handleRejectAmendment(am.id)} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Reject</button>
                            </div>
                          )}
                        </div>
                        {am.justification && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{am.justification}</p>}
                        {am.notes && <p className="text-xs text-gray-400 mt-1">Notes: {am.notes}</p>}
                        {/* Approver info */}
                        {am.approver && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                            <span>Approver: {am.approver.name || am.approver}</span>
                            {am.approved_at && <span>• Approved: {formatDateTime(am.approved_at)}</span>}
                          </div>
                        )}
                        {/* Amendment items */}
                        {am.items && am.items.length > 0 && (
                          <div className="mt-2 overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-gray-400 border-b dark:border-gray-600">
                                  <th className="py-1 pr-2">Item Code</th>
                                  <th className="py-1 pr-2">Description</th>
                                  <th className="py-1 pr-2 text-right">Orig Qty</th>
                                  <th className="py-1 pr-2 text-right">New Qty</th>
                                  <th className="py-1 pr-2 text-right">Orig Price</th>
                                  <th className="py-1 pr-2 text-right">New Price</th>
                                  <th className="py-1 text-right">Line Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {am.items.map((ami: any, idx: number) => (
                                  <tr key={idx} className="border-b dark:border-gray-700">
                                    <td className="py-1 pr-2 text-gray-600 dark:text-gray-400">{ami.item_code || '-'}</td>
                                    <td className="py-1 pr-2 text-gray-700 dark:text-gray-300">{ami.description || ami.name || '-'}</td>
                                    <td className="py-1 pr-2 text-right text-gray-500">{ami.original_qty ?? '-'}</td>
                                    <td className="py-1 pr-2 text-right text-gray-700 dark:text-gray-300">{ami.amended_qty ?? ami.quantity ?? '-'}</td>
                                    <td className="py-1 pr-2 text-right text-gray-500">{ami.original_price != null ? formatCurrency(ami.original_price) : '-'}</td>
                                    <td className="py-1 pr-2 text-right text-gray-700 dark:text-gray-300">{ami.amended_price != null ? formatCurrency(ami.amended_price) : (ami.unit_price != null ? formatCurrency(ami.unit_price) : '-')}</td>
                                    <td className="py-1 text-right font-medium text-gray-700 dark:text-gray-300">{ami.line_total != null ? formatCurrency(ami.line_total) : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {am.created_at && <span className="text-xs text-gray-400 dark:text-gray-500 block mt-1">{formatDateTime(am.created_at)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activity History */}
              <div className="mt-6">
                <button
                  type="button"
                  className="flex items-center text-gray-700 dark:text-gray-300 font-semibold focus:outline-none"
                  onClick={() => setIsHistoryVisible(!isHistoryVisible)}
                >
                  <svg
                    className={`w-5 h-5 mr-2 transition-transform ${isHistoryVisible ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  History
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({isHistoryVisible ? 'Hide' : 'Show'})
                  </span>
                </button>
                {isHistoryVisible && po.histories && po.histories.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {[...po.histories].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((hist) => (
                      <div key={hist.id} className="flex items-start gap-3 p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-700 dark:text-gray-300">{hist.user?.name || 'Unknown'}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{hist.action}</span>
                          </div>
                          {hist.comment && (
                            <p 
                              className="text-sm text-gray-500 dark:text-gray-400 mt-1"
                              dangerouslySetInnerHTML={{ __html: hist.comment }}
                            />
                          )}
                          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
                            {formatDateTime(hist.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : isHistoryVisible && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">No history entries for this purchase order.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 p-5 md:p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Reject Purchase Order #{po.order_number || po.id}</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason (required)"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-red-500 focus:border-red-500 h-28"
              rows={4}
            />
            <div className="mt-5 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Submitting...' : 'Submit Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Cancellation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 p-5 md:p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Request Order Cancellation</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              A cancellation request will be sent to the order's approvers. The order stays approved until all approvers approve.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (required)"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white h-24"
              rows={3}
            />
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Recreate order once cancelled?</label>
              <select
                value={cancelRecreate}
                onChange={(e) => setCancelRecreate(e.target.value as '0' | '1')}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
            <div className="mt-5 flex justify-end space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 rounded-md transition-colors"
              >
                No, Return
              </button>
              <button
                onClick={confirmCancel}
                disabled={!cancelReason.trim() || actionLoading}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Submitting...' : 'Yes, Request Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Cancellation Modal */}
      {showRejectCancelModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 p-5 md:p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Reject Cancellation</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              The order will remain approved and the owner/requester will be notified with your reason.
            </p>
            <textarea
              value={rejectCancelReason}
              onChange={(e) => setRejectCancelReason(e.target.value)}
              placeholder="Reason for rejecting the cancellation (required)"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-orange-500 focus:border-orange-500 h-28"
              rows={4}
            />
            <div className="mt-5 flex justify-end space-x-3">
              <button
                onClick={() => { setShowRejectCancelModal(false); setRejectCancelReason(''); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRejectCancel}
                disabled={!rejectCancelReason.trim() || actionLoading}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Submitting...' : 'Reject Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Document Modal */}
      {current && (
        <AddDocumentModal
          po={current}
          isOpen={isAddDocumentModalOpen}
          onClose={() => setIsAddDocumentModalOpen(false)}
          onDocumentUploaded={handleDocumentUploaded}
        />
      )}

      {/* Edit Document Modal */}
      {current && (
        <EditDocumentModal
          po={current}
          isOpen={isEditDocumentModalOpen}
          onClose={() => setIsEditDocumentModalOpen(false)}
          onDocumentUpdated={handleDocumentUpdated}
          documentType={editDocumentType}
          documentId={editDocumentId}
        />
      )}

      {/* Create GRN Modal */}
      {showCreateGrnModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create GRN</h3>
              <button onClick={() => setShowCreateGrnModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* GRN Meta Fields */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Received Date *</label>
                <input type="date" value={grnMeta.received_date} onChange={(e) => setGrnMeta({ ...grnMeta, received_date: e.target.value })} className="w-full mt-1 px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Received Time</label>
                <input type="time" value={grnMeta.received_time} onChange={(e) => setGrnMeta({ ...grnMeta, received_time: e.target.value })} className="w-full mt-1 px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Received By *</label>
                <input type="text" value={grnMeta.received_by} onChange={(e) => setGrnMeta({ ...grnMeta, received_by: e.target.value })} placeholder="Name" className="w-full mt-1 px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Notes</label>
                <input type="text" value={grnMeta.notes} onChange={(e) => setGrnMeta({ ...grnMeta, notes: e.target.value })} placeholder="Optional notes" className="w-full mt-1 px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
            </div>

            {grnItemForms.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No items available for GRN.</p>
            ) : (
              <>
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        <th className="py-2 pr-2">Item Code</th>
                        <th className="py-2 pr-2">Description</th>
                        <th className="py-2 pr-2 text-right">Ordered</th>
                        <th className="py-2 pr-2 text-right">Received Qty</th>
                        <th className="py-2 pr-2 text-right">Unit Price</th>
                        <th className="py-2 pr-2 text-center">VAT</th>
                        <th className="py-2 pr-2 text-right">Line Total</th>
                        <th className="py-2">Variance Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {grnItemForms.map((item, idx) => {
                        const qty = Number(item.received_qty) || 0;
                        const price = Number(item.unit_price) || 0;
                        const lineTotal = qty * price;
                        const variance = qty > 0 && Number(item.ordered_qty) > 0 ? qty - Number(item.ordered_qty) : 0;
                        return (
                          <tr key={idx} className={variance !== 0 && qty > 0 ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}>
                            <td className="py-2 pr-2 text-xs text-gray-600 dark:text-gray-400">{item.item_code || '-'}</td>
                            <td className="py-2 pr-2 text-xs text-gray-700 dark:text-gray-300">{item.description || `#${item.po_line_id}`}</td>
                            <td className="py-2 pr-2 text-right text-xs text-gray-600 dark:text-gray-400">{item.ordered_qty}</td>
                            <td className="py-2 pr-2">
                              <input
                                type="number"
                                placeholder="0"
                                value={item.received_qty}
                                onChange={(e) => setGrnItemForms(grnItemForms.map((f, i) => i === idx ? { ...f, received_qty: e.target.value } : f))}
                                className="w-20 px-2 py-1 text-xs rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                              />
                            </td>
                            <td className="py-2 pr-2">
                              <input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => setGrnItemForms(grnItemForms.map((f, i) => i === idx ? { ...f, unit_price: e.target.value } : f))}
                                className="w-24 px-2 py-1 text-xs rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                              />
                            </td>
                            <td className="py-2 pr-2 text-center">
                              <input
                                type="checkbox"
                                checked={item.vatable}
                                onChange={(e) => setGrnItemForms(grnItemForms.map((f, i) => i === idx ? { ...f, vatable: e.target.checked } : f))}
                                className="rounded border-gray-300 dark:border-gray-600"
                              />
                            </td>
                            <td className="py-2 pr-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">
                              R{lineTotal.toFixed(2)}
                              {variance !== 0 && qty > 0 && (
                                <span className={`ml-1 text-xs ${variance > 0 ? 'text-orange-500' : 'text-red-500'}`}>(±{variance})</span>
                              )}
                            </td>
                            <td className="py-2">
                              <input
                                type="text"
                                placeholder="If variance"
                                value={item.variance_reason}
                                onChange={(e) => setGrnItemForms(grnItemForms.map((f, i) => i === idx ? { ...f, variance_reason: e.target.value } : f))}
                                disabled={variance === 0 || qty === 0}
                                className="w-28 px-2 py-1 text-xs rounded border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-40"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Totals Summary */}
                {(() => {
                  const totals = computeGrnTotals(grnItemForms);
                  return (
                    <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between text-sm py-0.5">
                        <span className="text-gray-600 dark:text-gray-400">Net Total:</span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">R{totals.net.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm py-0.5">
                        <span className="text-gray-600 dark:text-gray-400">VAT (15%):</span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">R{totals.vat.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm py-0.5 border-t border-gray-200 dark:border-gray-600 mt-1 pt-1">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Gross Total:</span>
                        <span className="font-bold text-gray-900 dark:text-white">R{totals.gross.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreateGrnModal(false)} className="px-4 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100">Cancel</button>
              <button onClick={() => handleCreateGrn(false)} disabled={actionLoading} className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50">
                {actionLoading ? 'Saving...' : 'Save Draft'}
              </button>
              <button onClick={() => handleCreateGrn(true)} disabled={actionLoading} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">
                {actionLoading ? 'Saving...' : 'Save & Finalize'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GRN Detail Modal */}
      {showGrnDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 rounded-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">GRN Details</h3>
              <button onClick={() => { setShowGrnDetailModal(false); setGrnDetail(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {grnDetailLoading ? (
              <p className="text-sm text-gray-400 py-8 text-center">Loading GRN details...</p>
            ) : grnDetail ? (
              <>
                {/* GRN Meta Info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/30">
                  <div>
                    <span className="text-xs text-gray-400 block">GRN Number</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{grnDetail.grn_number || `#${grnDetail.id}`}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block">Status</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${grnDetail.status === 'finalized' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>{grnDetail.status}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block">Received Date</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{grnDetail.received_date ? formatDate(grnDetail.received_date) : '-'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block">Received By</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{grnDetail.received_by || '-'}</span>
                  </div>
                  {grnDetail.received_time && (
                    <div>
                      <span className="text-xs text-gray-400 block">Time</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{grnDetail.received_time}</span>
                    </div>
                  )}
                  {grnDetail.notes && (
                    <div className="col-span-2">
                      <span className="text-xs text-gray-400 block">Notes</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{grnDetail.notes}</span>
                    </div>
                  )}
                </div>

                {/* GRN Items Table */}
                {grnDetail.items && grnDetail.items.length > 0 && (
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          <th className="py-2 pr-2">Item Code</th>
                          <th className="py-2 pr-2">Description</th>
                          <th className="py-2 pr-2 text-right">Ordered</th>
                          <th className="py-2 pr-2 text-right">Received</th>
                          <th className="py-2 pr-2 text-right">Unit Price</th>
                          <th className="py-2 pr-2 text-center">VAT</th>
                          <th className="py-2 pr-2 text-right">Line Total</th>
                          <th className="py-2 text-right">Variance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                        {grnDetail.items.map((item: any) => {
                          const variance = Number(item.received_qty) - Number(item.ordered_qty);
                          const lineTotal = Number(item.received_qty) * Number(item.unit_price);
                          return (
                            <tr key={item.id} className={variance !== 0 ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}>
                              <td className="py-2 pr-2 text-xs text-gray-600 dark:text-gray-400">{item.item_code || '-'}</td>
                              <td className="py-2 pr-2 text-xs text-gray-700 dark:text-gray-300">{item.description || '-'}</td>
                              <td className="py-2 pr-2 text-right text-xs text-gray-600 dark:text-gray-400">{item.ordered_qty}</td>
                              <td className="py-2 pr-2 text-right text-xs text-gray-700 dark:text-gray-300">{item.received_qty}</td>
                              <td className="py-2 pr-2 text-right text-xs text-gray-700 dark:text-gray-300">R{Number(item.unit_price).toFixed(2)}</td>
                              <td className="py-2 pr-2 text-center text-xs">{item.vatable ? '✓' : '-'}</td>
                              <td className="py-2 pr-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">R{lineTotal.toFixed(2)}</td>
                              <td className="py-2 text-right text-xs">
                                {variance !== 0 ? (
                                  <span className={variance > 0 ? 'text-orange-500' : 'text-red-500'}>{variance > 0 ? '+' : ''}{variance}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totals */}
                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between text-sm py-0.5">
                    <span className="text-gray-600 dark:text-gray-400">Net Total:</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">R{Number(grnDetail.net_total || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-0.5">
                    <span className="text-gray-600 dark:text-gray-400">VAT (15%):</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">R{Number(grnDetail.vat_total || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-0.5 border-t border-gray-200 dark:border-gray-600 mt-1 pt-1">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Gross Total:</span>
                    <span className="font-bold text-gray-900 dark:text-white">R{Number(grnDetail.gross_total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400 py-8 text-center">Failed to load GRN details.</p>
            )}
          </div>
        </div>
      )}

      {/* Create Credit Note Modal */}
      {showCreateCreditNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Credit Note</h3>
              <button onClick={() => setShowCreateCreditNoteModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Credit Note Number</label>
                <input type="text" value={newCreditNote.credit_note_number} onChange={(e) => setNewCreditNote({ ...newCreditNote, credit_note_number: e.target.value })} placeholder="Auto-generated if blank" className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Credit Note Date</label>
                <input type="date" value={newCreditNote.credit_note_date} onChange={(e) => setNewCreditNote({ ...newCreditNote, credit_note_date: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Reason</label>
                <textarea value={newCreditNote.reason} onChange={(e) => setNewCreditNote({ ...newCreditNote, reason: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div className="flex gap-3">
                <select value={newCreditNote.vat_type} onChange={(e) => setNewCreditNote({ ...newCreditNote, vat_type: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                  <option value="inclusive">VAT Inclusive</option>
                  <option value="exclusive">VAT Exclusive</option>
                </select>
                <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                  <input type="checkbox" checked={newCreditNote.non_vat} onChange={(e) => setNewCreditNote({ ...newCreditNote, non_vat: e.target.checked })} />
                  Non-VAT
                </label>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowCreateCreditNoteModal(false)} className="px-4 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100">Cancel</button>
              <button onClick={handleCreateCreditNote} disabled={actionLoading} className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50">
                {actionLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Amendment Modal */}
      {showCreateAmendmentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Request PO Amendment</h3>
              <button onClick={() => setShowCreateAmendmentModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Invoice Amount</label>
                <input type="number" value={newAmendment.invoice_amount} onChange={(e) => setNewAmendment({ ...newAmendment, invoice_amount: e.target.value })} placeholder="Actual invoice amount" className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Justification *</label>
                <textarea value={newAmendment.justification} onChange={(e) => setNewAmendment({ ...newAmendment, justification: e.target.value })} rows={3} placeholder="Why is this amendment needed?" className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Approver *</label>
                <select value={newAmendment.approved_by} onChange={(e) => setNewAmendment({ ...newAmendment, approved_by: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                  <option value="">Select approver...</option>
                  {(po.approvers || []).map((a: any) => (
                    <option key={a.id} value={a.user?.id || a.id}>{a.user?.name || `User #${a.id}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Notes</label>
                <input type="text" value={newAmendment.notes} onChange={(e) => setNewAmendment({ ...newAmendment, notes: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowCreateAmendmentModal(false)} className="px-4 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100">Cancel</button>
              <button onClick={handleCreateAmendment} disabled={actionLoading || !newAmendment.justification || !newAmendment.approved_by} className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                {actionLoading ? 'Creating...' : 'Request Amendment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Credit Note Modal */}
      {showEditCreditNoteModal && editingCreditNote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 rounded-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Credit Note</h3>
              <button onClick={() => { setShowEditCreditNoteModal(false); setEditingCreditNote(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Credit Note Number</label>
                <input type="text" value={editCreditNoteForm.credit_note_number} onChange={(e) => setEditCreditNoteForm({ ...editCreditNoteForm, credit_note_number: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Credit Note Date</label>
                  <input type="date" value={editCreditNoteForm.credit_note_date} onChange={(e) => setEditCreditNoteForm({ ...editCreditNoteForm, credit_note_date: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Delivery Date</label>
                  <input type="date" value={editCreditNoteForm.delivery_date} onChange={(e) => setEditCreditNoteForm({ ...editCreditNoteForm, delivery_date: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Received By</label>
                <input type="text" value={editCreditNoteForm.received_by} onChange={(e) => setEditCreditNoteForm({ ...editCreditNoteForm, received_by: e.target.value })} placeholder="Name" className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Reason</label>
                <textarea value={editCreditNoteForm.reason} onChange={(e) => setEditCreditNoteForm({ ...editCreditNoteForm, reason: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div className="flex gap-3">
                <select value={editCreditNoteForm.vat_type} onChange={(e) => setEditCreditNoteForm({ ...editCreditNoteForm, vat_type: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                  <option value="inclusive">VAT Inclusive</option>
                  <option value="exclusive">VAT Exclusive</option>
                </select>
                <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                  <input type="checkbox" checked={editCreditNoteForm.non_vat} onChange={(e) => setEditCreditNoteForm({ ...editCreditNoteForm, non_vat: e.target.checked })} />
                  Non-VAT
                </label>
              </div>
              {/* Line items display */}
              {editingCreditNote.line_items && editingCreditNote.line_items.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Line Items</h4>
                  <div className="space-y-1">
                    {editingCreditNote.line_items.map((li: any) => (
                      <div key={li.id} className="flex items-center justify-between text-xs p-2 rounded bg-gray-50 dark:bg-gray-700/30">
                        <div>
                          <span className="text-gray-700 dark:text-gray-300">{li.description}</span>
                          <span className="ml-2 text-gray-400">Qty: {li.quantity}</span>
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${li.vat_type === 'inclusive' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>{li.vat_type}</span>
                        </div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(li.total_amount || li.total_price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => { setShowEditCreditNoteModal(false); setEditingCreditNote(null); }} className="px-4 py-2 rounded-lg border bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100">Cancel</button>
              <button onClick={handleUpdateCreditNote} disabled={actionLoading} className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50">
                {actionLoading ? 'Updating...' : 'Update Credit Note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetailPage;
